import { css } from '@emotion/react';
import { AccountCircle, ArrowBack } from '@mui/icons-material';
import {
  ButtonBase,
  IconButton,
  IconButtonProps,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  Theme,
  useMediaQuery,
} from '@mui/material';
import Drawer from '@mui/material/Drawer';
import { GridMenuIcon } from '@mui/x-data-grid';
import Image from 'next/image';
import Link from 'next/link';
import { signOut, useSession } from 'next-auth/react';
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { DrawerNavStyles as styles } from './header.style';
import { TabsData } from './HeaderUtils';
import { SupportModal } from './SupportMenu';
import { EngagementEvents, logKariusEvent } from '../Analytics';
import KariusLogo from '../assets/images/svg/KariusLogo';
import KariusMobile from '../assets/images/svg/KariusMobile';
import { Resources } from '../auth/authTypes';
import { checkAccess } from '../auth/checkAccess';
import Colors from '../theme/colors';

const DrawerLinkCss = css`
  & div {
    color: ${Colors.blue} !important;
  }
  &:hover {
    background-color: ${Colors.lightTeal};
  }
`;

const SelectedDrawerLinkCss = css`
  background-color: ${Colors.blue} !important;
  color: ${Colors.white} !important;
  & div {
    color: ${Colors.white} !important;
  }
  & div span {
    color: ${Colors.white} !important;
  }
`;

interface DrawerNavProps {
  selectedTab?: Resources;
}

const mobileHeaderID = 'mobile-header-content';

const DrawerNav = React.forwardRef<HTMLDivElement, DrawerNavProps>(({ selectedTab }, ref) => {
  return (
    <div css={styles.header} ref={ref} id={mobileHeaderID}>
      {selectedTab !== Resources.ordersListView && (
        <>
          <div css={styles.menu} id='header-drawer-nav-button'>
            <MobileDrawerButton selectedTab={selectedTab} />
          </div>
          <KariusMobile
            style={{
              marginTop: '10px',
            }}
          />
          <SupportModal />
        </>
      )}
    </div>
  );
});

DrawerNav.displayName = 'DrawerNa';

export default DrawerNav;

/**
 * Portals components into the header of the app
 * @example
 * // Places the <h1> tag into the mobile header of the app
 * <MobileHeaderPortal
 *   Component={
 *     <h1>Hi</h1>
 *   }
 * />
 */
export const MobileHeaderPortal = ({ children }: { children: React.ReactNode }): React.ReactPortal | null => {
  const getHeader = () => document.getElementById(mobileHeaderID);
  const [header, setHeader] = useState(getHeader());

  if (!header && getHeader()) {
    setHeader(getHeader());
  }

  return header ? ReactDOM.createPortal(children, header) : null;
};

export const MobileDrawerButton = React.forwardRef<HTMLButtonElement, DrawerNavProps>(({ selectedTab }, ref) => {
  const [open, setOpen] = useState(false);
  const { data: session } = useSession();
  const matchesSm = useMediaQuery<Theme>((theme) => {
    return theme.breakpoints.down('md');
  });
  const onClose = () => setOpen(false);
  const user = session?.user;

  return (
    <>
      <div css={styles.menu} id='header-drawer-nav-button'>
        <IconButton
          aria-label='drawer-menu-button'
          onClick={() => {
            logKariusEvent(EngagementEvents.global_menu_tapped);
            setOpen(true);
          }}
          ref={ref}
          color='inherit'
          sx={{ minWidth: '0px', marginLeft: '-8px' }}
        >
          <GridMenuIcon />
        </IconButton>
      </div>
      {matchesSm && ( // mobile style
        <Drawer
          aria-label='navigation-drawer'
          anchor='left'
          open={open}
          onClose={onClose}
          BackdropProps={{
            style: { backgroundColor: 'rgba(0,0,0,0)' },
          }}
        >
          <Logo onClose={onClose} />
          <List
            style={{
              padding: 0,
              color: Colors.blue,
            }}
          >
            {user && (
              <ListItemButton selected css={SelectedDrawerLinkCss}>
                <ListItemText primary={user.name} secondary={user.institution?.name} css={styles.userLabel} />
              </ListItemButton>
            )}
            <DrawerItem resourceName={Resources.eTRF} selectedTab={selectedTab} onClose={onClose} />
            <DrawerItem resourceName={Resources.ordersListView} selectedTab={selectedTab} onClose={onClose} />
            <DrawerItem resourceName={Resources.kitOrdering} selectedTab={selectedTab} onClose={onClose} />
            <DrawerItem resourceName={Resources.users} selectedTab={selectedTab} onClose={onClose} />
            {user && (
              <ListItemButton
                onClick={() => {
                  logKariusEvent(EngagementEvents.sign_out_tapped);
                  signOut({ callbackUrl: '/' });
                }}
                css={DrawerLinkCss}
              >
                <ListItemIcon>
                  {user?.gravatarUrl ? (
                    <Image src={user.gravatarUrl} alt='Profile Image' width={26} height={26} css={styles.userIcon} />
                  ) : (
                    <AccountCircle fontSize='medium' color='primary' />
                  )}
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ color: 'inherit' }}>Sign out</ListItemText>
              </ListItemButton>
            )}
          </List>
        </Drawer>
      )}
    </>
  );
});

MobileDrawerButton.displayName = 'MobileDrawerButton';

interface DrawerItemProps {
  selectedTab?: Resources;
  resourceName: Resources;
  onClose: () => void;
}

function DrawerItem({ resourceName, selectedTab, onClose }: DrawerItemProps) {
  const { data: session } = useSession();
  const { href, label, icon, event } = TabsData[resourceName];

  if (!session?.user || !checkAccess(session.user.role, session.user.effectivePermissions, resourceName)) {
    return null;
  }

  return (
    <Link href={href} passHref={true}>
      <ListItemButton
        selected={resourceName === selectedTab}
        onClick={() => {
          logKariusEvent(EngagementEvents.nav_link_tapped, { link: event });
          onClose();
        }}
        css={resourceName === selectedTab ? SelectedDrawerLinkCss : DrawerLinkCss}
      >
        <ListItemIcon> {icon} </ListItemIcon>
        <ListItemText primaryTypographyProps={{ color: 'inherit' }}>{label} </ListItemText>
      </ListItemButton>
    </Link>
  );
}

export const NavBackButton: React.FC<IconButtonProps> = (props) => {
  return (
    <BackButtonCont {...props}>
      <ArrowBack />
    </BackButtonCont>
  );
};

const BackButtonCont = styled(IconButton)(() => ({
  '& svg': {
    color: '#ffffff',
  },
  '&:hover': {
    backgroundColor: Colors.blue,
  },
  backgroundColor: Colors.blue,
  // Account for padding around button so
  // button still has padding for click area
  // but also aligns nicely in header
  marginLeft: '-8px',
}));

function Logo({ onClose }: { onClose: () => void }) {
  return (
    <div css={styles.iconContainer}>
      <Link href='/' passHref={true}>
        <ButtonBase
          sx={{
            padding: '10px',
          }}
          onClick={onClose}
        >
          <KariusLogo />
        </ButtonBase>
      </Link>
    </div>
  );
}
